import { useState } from 'react';
import { Download, Info, MoreVertical, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RAG_BASE_URL } from '@/config/rag';
import { getAuthToken } from '@/features/auth/session';
import { getDownloadUrl, RagApiError } from '@/services/ragApi';

import type { DocumentResponse } from '../../types';

interface Props {
  collectionId: string;
  document: DocumentResponse;
  onViewDetails: (doc: DocumentResponse) => void;
  onRequestDelete: (doc: DocumentResponse) => void;
}

/**
/** Menu contextual por documento — Abrir arquivo / Ver detalhes / Remover. */
export function DocumentRowMenu({ collectionId, document, onViewDetails, onRequestDelete }: Props) {
  const [opening, setOpening] = useState(false);
  // "pending" é ambíguo (URL emitida, mas o PUT pode não ter concluído ainda)
  // — o arquivo já existe no bucket em qualquer status posterior a esse.
  const canOpen = document.status !== 'pending' && !opening;

  async function handleOpen() {
    const token = getAuthToken();
    if (!token) return;
    setOpening(true);
    try {
      const { download_url } = await getDownloadUrl(RAG_BASE_URL, token, collectionId, document.id);
      window.open(download_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const msg = err instanceof RagApiError ? err.message : 'Não foi possível abrir o arquivo.';
      toast.error(msg);
    } finally {
      setOpening(false);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="size-7"
          onClick={(e) => e.stopPropagation()}
          aria-label="Ações do documento"
        >
          <MoreVertical className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onClick={(e) => e.stopPropagation()}
        // Ao fechar, o Radix devolve o foco para o botão ⋮. Como o painel de
        // detalhes é não-modal, esse foco pousando fora dele conta como
        // "interação externa" e o fecha no mesmo instante em que abriu. Segurar
        // o foco aqui é o que impede o painel de piscar quando aberto pelo menu.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenuItem disabled={!canOpen} onSelect={() => void handleOpen()}>
          <Download />
          Abrir arquivo
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onViewDetails(document)}>
          <Info />
          Ver detalhes
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive data-highlighted:bg-destructive/10 data-highlighted:text-destructive"
          onSelect={() => onRequestDelete(document)}
        >
          <Trash2 />
          Remover
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
